"use client";

import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";

const StyledSvg = styled("svg")({
  width: "100%",
  height: "100%",
  overflow: "visible",
});

const HighlightRect = styled("rect")({
  fill: "lightblue",
  opacity: 0.3,
});

const Dot = styled("circle")({
  fill: "black",
  r: 5,
});

const Arc = styled("path")({
  fill: "none",
  stroke: "black",
  strokeWidth: 3,
});

export const DFIJ_BLO = ({
  exercise,
  isRunning,
  velocity,
  dotPatternInterval = 2,
}) => {
  const [svgWidth, setSvgWidth] = useState(0);
  const [textLines, setTextLines] = useState<string[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(exercise.content);
  const [lines, setLines] = useState(exercise.lineas);
  const lineHeight = 40;
  const fontSize = 16;
  const dotRadius = 4;
  const arcHeight = 60;
  const dotVerticalPosition = lineHeight / 4 - 5;
  const [velocidad, setVelocidad] = useState(null);
  const dotRowSpacing = 10; // Espacio adicional arriba y abajo de las filas de puntos sin arcos

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setSvgWidth(containerRef.current.clientWidth);
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    if (svgWidth > 0) {
      const words = text.split(" ");
      const newLines: string[] = [];
      const charsPerLine = Math.floor(svgWidth / (fontSize * 0.6));

      let currentLine = "";
      let lineCount = 0;
      let wordIndex = startIndex;

      while (lineCount < lines) {
        if (currentLine.length + words[wordIndex].length + 1 <= charsPerLine) {
          currentLine += (currentLine ? " " : "") + words[wordIndex];
          wordIndex = (wordIndex + 1) % words.length;
        } else {
          if (currentLine.trim() !== "") {
            newLines.push(currentLine.trim());
            lineCount++;
            currentLine = "";
          }
          if (words[wordIndex].length > charsPerLine) {
            newLines.push(words[wordIndex]);
            lineCount++;
            wordIndex = (wordIndex + 1) % words.length;
          }
        }

        if (wordIndex === startIndex && currentLine === "") {
          break;
        }
      }

      if (currentLine.trim() !== "" && lineCount < lines) {
        newLines.push(currentLine.trim());
      }

      while (newLines.length < lines) {
        newLines.push("");
      }
      const numPalabras = newLines.join(" ").split(" ").length
      let velocidadTemp = ((60 / velocity) * 1000);
      velocidadTemp = velocidadTemp * (numPalabras / exercise.lineas);
      setVelocidad(velocidadTemp);
      setTextLines(newLines);
    }
  }, [text, lines, svgWidth, startIndex]);

  useEffect(() => {
    let timer = null;
    if (isRunning) {
      timer = setInterval(() => {
        setHighlightIndex((prevIndex) => {
          let nextIndex = (prevIndex + 1) % lines;
          while (textLines[nextIndex]?.trim() === "" && nextIndex !== prevIndex) {
            nextIndex = (nextIndex + 1) % lines;
          }

          if (nextIndex === 0) {
            setStartIndex((prevStartIndex) => {
              const words = text.split(" ");
              let newStartIndex = prevStartIndex;
              let wordCount = 0;

              textLines.forEach((line) => {
                wordCount += line.split(" ").length;
              });

              newStartIndex = (prevStartIndex + wordCount) % words.length;

              return newStartIndex;
            });
          }

          return nextIndex;
        });
      }, velocidad);
    } 
    return () => { if (timer) clearInterval(timer)};
  }, [lines, textLines, text, isRunning, velocidad]);

  const nonEmptyLines = textLines.filter((line) => line.trim() !== "");
  const actualHeight = nonEmptyLines.length * lineHeight;

  const generateArcPath = (startX: number, endX: number, height: number) => {
    const midX = (startX + endX) / 2;
    const controlY = -height + dotVerticalPosition;
    return `M ${startX} ${dotVerticalPosition} Q ${midX} ${controlY} ${endX} ${dotVerticalPosition}`;
  };

  const getLinePosition = (lineIndex: number) => {
    let position = lineIndex * lineHeight;
    const dotRowsBeforeCurrent = Math.floor(lineIndex / dotPatternInterval);
    position += dotRowsBeforeCurrent * (dotRowSpacing * 2); // Multiplicamos por 2 para considerar el espacio arriba y abajo
    return position;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        overflow: "visible",
        background: "#ffffff",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: actualHeight + (Math.floor(nonEmptyLines.length / dotPatternInterval) * dotRowSpacing * 2),
          position: "relative",
        }}
      >
        <StyledSvg>
          {nonEmptyLines.map((_, lineIndex) => {
            if (lineIndex % dotPatternInterval === 0) {
              const yPosition = getLinePosition(lineIndex) + dotVerticalPosition;
              return (
                <g key={lineIndex}>
                  {lineIndex === 0 && (
                    <>
                      <Arc d={generateArcPath(svgWidth / 8, svgWidth / 2, arcHeight)} />
                      <Arc d={generateArcPath(svgWidth / 2, (svgWidth * 7) / 8, arcHeight)} />
                    </>
                  )}
                  <Dot cx={svgWidth / 8} cy={yPosition} />
                  <Dot cx={svgWidth / 2} cy={yPosition} />
                  <Dot cx={(svgWidth * 7) / 8} cy={yPosition} />
                </g>
              );
            }
            return null;
          })}

          {textLines.map(
            (line, index) =>
              line.trim() !== "" && (
                <text
                  key={index}
                  x="0"
                  y={getLinePosition(index) + (lineHeight / 2)}
                  fontSize={`${fontSize}px`}
                  textAnchor="start"
                  dominantBaseline="middle"
                  textLength={svgWidth}
                  lengthAdjust="spacingAndGlyphs"
                >
                  {line}
                </text>
              )
          )}
          {nonEmptyLines.length > 0 && (
            <HighlightRect
              x={0}
              y={getLinePosition(highlightIndex)}
              width={svgWidth}
              height={lineHeight}
            />
          )}
        </StyledSvg>
      </Box>
    </Box>
  );
};

export default DFIJ_BLO;

